import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DateUtils, FileUtils, IButton, ToasterService, TranslateService } from '@devinforius/i-essentials';
import { IColumn } from '@devinforius/i-table';
import { Historic } from '@features/valve/models';
import { ValveService } from '@features/valve/services';
import { HistoricAction } from '@shared/enums/historic-action.enum';
import { LazyLoadEvent } from 'primeng/api';

@Component({
  selector: 'app-valve-historic',
  templateUrl: './valve-historic.component.html',
  styleUrl: './valve-historic.component.scss'
})
export class ValveHistoricComponent implements OnInit {
  private translateService: TranslateService = inject(TranslateService);
  private toasterService: ToasterService = inject(ToasterService);
  private router: Router = inject(Router);
  public valveService: ValveService = inject(ValveService);

  public titleButtons!: IButton[];
  public columns!: IColumn[];
  public items!: Historic[];
  public loading: boolean = true;
  public tableLoading: boolean = false;
  public preventLoading: boolean = true;

  public ngOnInit(): void {
    this.initColumns();
    this.initButtons();
    this.loadHistoric();
  }

  public back(): void {
    this.router.navigate(['valves']);
  }

  public onTableUpdate(event: LazyLoadEvent): void {
    if (!this.preventLoading) {
      this.tableLoading = true;

      this.valveService.currentHistoricalPagination = {
        ...this.valveService.currentHistoricalPagination,
        pageSize: event.rows || 50,
        pageNumber: event.first === 0 ? 1 : (event.first || 1) / (event.rows || 50) + 1
      };

      this.loadHistoric();
    } else {
      this.preventLoading = false;
    }
  }

  private initButtons(): void {
    this.titleButtons = [{
      text: this.translateService.translate('valves.historic.button'),
      icon: 'icon-historic',
      iconPosition: 'right',
      styleClass: 'blue',
      click: () => this.exportHistoric()
    }];
  }

  private initColumns(): void {
    this.columns = [
      {
        field: 'description',
        header: this.translateService.translate('valves.historic.list.action'),
        format: (value: string, item: Historic) => {
          switch(item.actionCode) {
            case HistoricAction.archive:
              return `<i class="bi bi-archive-fill"></i>  ${value}`;
            case HistoricAction.create:
              return `<i class="bi bi-plus-circle-fill"></i>  ${value}`;
            case HistoricAction.published:
              return `<i class="icon-stamp-black"></i>  ${value}`;
            case HistoricAction.unpublish:
              return `<i class="bi"></i>  ${value}`;
            case HistoricAction.update:
              return `<i class="bi bi-pen-fill"></i>  ${value}`;
            default:
              return value;
          }
        }
      },
      {
        field: 'title',
        header: this.translateService.translate('valves.historic.list.object')
      },
      {
        field: 'userFullName',
        header: this.translateService.translate('valves.historic.list.user')
      },
      {
        field: 'dateAction',
        header: this.translateService.translate('valves.historic.list.date'),
        format: (value: string) => {
          return DateUtils.format(value, 'DD/MM/YYYY à HH:mm');
        }
      }
    ];
  }

  private exportHistoric(): void {
    this.valveService.exportHistorical().subscribe({
      next: (file) => {
        FileUtils.download(file);
      },
      error: () => {
        this.toasterService.push('error', this.translateService.translate('global.error'), this.translateService.translate('valves.error.export'));
      }
    });
  }

  private loadHistoric(): void {
    this.valveService.getHistorical().subscribe({
      next: (response: Historic[]) => {
        this.items = response;
        this.loading = false;
        this.tableLoading = false;
      },
      error: () => {
        this.loading = false;
        this.tableLoading = false;
        this.toasterService.push('error', this.translateService.translate('global.error'), this.translateService.translate('valves.error.get-historical'));
      }
    });
  }
}
